import StatusCode from 'status-code-enum';

import { adViewApi } from '../../../../api/adViewApi';
import { fireToast } from 'helpers/Toasts';
import { ShowPhoneNumberButton } from 'components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton';
import { pushToDataLayer } from 'services/gtmService';

import getConfig from 'next/config';
import { useScript } from 'hooks/UseScript';
import { getRecaptchaToken } from 'helpers/reCaptcha';
import { useBraze } from 'hooks/useBraze';
import { useAuthOptions } from 'hooks/useAuthOptions';
import type { SellerTypes } from 'components/PageWrappers/AdDetails/types';

const {
  publicRuntimeConfig: { GOOGLE_RECAPTCHA, INTEGRATION_TESTING },
} = getConfig();

interface RevealPhoneNumberProps {
  adId: number;
  showNumberTextVariant?: boolean;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  trackingId?: string;
  clickSource?: string;
  isPrimaryButton?: boolean;
  campaignId?: string | null;
  disabled?: boolean;
  sectionName?: string;
  sellerType?: SellerTypes;
}

function RevealPhoneNumber({
  adId,
  showNumberTextVariant = false,
  size = 'SMALL',
  trackingId = 'reveal-number',
  clickSource = '',
  isPrimaryButton = false,
  campaignId,
  disabled,
  sectionName,
  sellerType,
}: RevealPhoneNumberProps) {
  useScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA}`,
    'recaptcha',
    [adId],
  );

  const { handleLogin } = useAuthOptions();
  const { logBrazeRevealPhoneEvent } = useBraze();

  async function handleReveal() {
    try {
      const captchaToken = await getRecaptchaToken(
        'phone_reveal',
        GOOGLE_RECAPTCHA,
        INTEGRATION_TESTING,
      );
      const { data } = await adViewApi.getPhoneNumber(
        adId,
        captchaToken,
        campaignId,
      );

      pushToDataLayer({ event: 'phone_reveal', source: clickSource });
      logBrazeRevealPhoneEvent(sectionName, sellerType);

      return data.phoneNumber;
    } catch (error) {
      if (
        error.response &&
        error.response.status === StatusCode.ClientErrorUnauthorized
      ) {
        handleLogin();
      } else {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
      return false;
    }
  }

  async function handleOnCall() {
    try {
      pushToDataLayer({ event: 'click_to_call', source: clickSource });
      await adViewApi.trackAdCallNumber(adId, campaignId);
    } catch (_error) {}
  }

  return (
    <ShowPhoneNumberButton
      adId={adId}
      onReveal={handleReveal}
      showNumberTextVariant={showNumberTextVariant}
      size={size}
      trackingId={trackingId}
      onCall={handleOnCall}
      isPrimaryButton={isPrimaryButton}
      disabled={disabled}
    />
  );
}

export { RevealPhoneNumber, type RevealPhoneNumberProps };
